import { useTranslation } from "react-i18next";
import { MarketButton, MarketDivider } from "@market/react";
import { Card } from "/src/protos/squareup/bbdebit/cardmanagement";
import { Props as ListItemProps } from "/src/components/ui/list/ListItem";
import { PreviewContentCard } from "/src/components/ui/content-card/PreviewContentCard";
import { List } from "/src/components/ui/list/List";
import { ContentCard } from "/src/components/ui/content-card/ContentCard";
import { isActivatable } from "/src/util/card-v2";
import styles from "./CardManagement.css";
import { CardCustomization } from "/src/protos/squareup/bizbank/service";
import { Country } from "/src/protos/squareup/common/countries";

export type CardManagementListItem = Omit<ListItemProps, "subtext">;

export interface CardManagementProps {
  readonly card: Card;
  readonly cardCustomization?: CardCustomization;
  readonly cardSettings: CardManagementListItem[];
  readonly helpSettings: CardManagementListItem[];
  readonly showCardDetails: boolean;
  readonly onActivateCardClick: () => void;
}

export function CardManagement({
  card,
  cardCustomization,
  cardSettings,
  helpSettings,
  showCardDetails,
  onActivateCardClick,
}: CardManagementProps) {
  const { t } = useTranslation("card-settings");
  const previewSecondaryContent = isActivatable(card) ? (
    <MarketButton rank="primary" className={styles.button} onClick={onActivateCardClick}>
      {t("button.activateCard")}
    </MarketButton>
  ) : undefined;

  const showCardSettings =
    cardSettings.length > 0 && cardSettings.some((setting) => !setting.hidden);

  const isUsSeller = card?.fulfillment?.address?.countryCode === Country.US;

  return (
    <div className={styles.container}>
      <PreviewContentCard
        card={card}
        cardCustomization={cardCustomization}
        previewSecondaryContent={previewSecondaryContent}
        showCardDetails={showCardDetails}
      >
        {showCardSettings ? <List items={cardSettings} /> : null}
      </PreviewContentCard>
      <MarketDivider className={styles.divider}></MarketDivider>
      {helpSettings.length > 0 ? (
        <ContentCard>
          <h3 className={styles.helpHeader}>{t("section.help.title")}</h3>
          <List items={helpSettings} />
        </ContentCard>
      ) : (
        <></>
      )}
      {isUsSeller && <p className={styles.disclaimer}>{t("section.settings.disclaimer")}</p>}
    </div>
  );
}
